import { nanoid } from 'nanoid';
import { captureException } from '../../services/logService';
import React, { useEffect } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, NodeViewProps } from '@tiptap/react';
import { RenderImagePreview } from '../renderImagePreview';
import { getFirebaseStorage } from '../../utils/firebase';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, UploadTask } from '@firebase/storage';
import UserPreferenceSingleton from '../../helpers/userPreferenceSingleton';
import { compressImageFile, DEFAULT_UPLOAD_FOLDER } from '../../helpers/common';
import { IMAGE_COMPRESSION } from '../../global/constants';

// Keep pasted File objects outside of the ProseMirror document (attrs must be serializable)
const PastedFileRegistry: Map<string, File> = new Map();
export const registerPastedFile = (nodeId: string, file: File) => {
  PastedFileRegistry.set(nodeId, file);
};

export const clearAllPastedFiles = (): void => {
  PastedFileRegistry.clear();
};

// Maintain active upload tasks for control (pause/resume/cancel)
type UploadController = {
  task: UploadTask;
  unsubscribe: () => void;
};

const UploadTaskRegistry: Map<string, UploadController> = new Map();

// Lightweight event target to notify listeners when registry changes
const UploadEvents = new EventTarget();
const notifyUploadRegistryChanged = () => UploadEvents.dispatchEvent(new Event('change'));

export const onUploadRegistryChange = (listener: () => void): (() => void) => {
  const handler = () => listener();
  UploadEvents.addEventListener('change', handler);
  return () => UploadEvents.removeEventListener('change', handler);
};

export const isAnyImageUploading = (): boolean => UploadTaskRegistry.size > 0;

// Track ownership of upload nodes by editor instance
const NodeOwner: Map<string, string> = new Map();

export const isAnyImageUploadingFor = (ownerId: string): boolean => {
  let hasUploading = false;
  UploadTaskRegistry.forEach((_, nodeId) => {
    if (NodeOwner.get(nodeId) === ownerId) hasUploading = true;
  });
  return hasUploading;
};

export const cancelAllImageUploads = (): void => {
  UploadTaskRegistry.forEach(({ task, unsubscribe }) => {
    try {
      unsubscribe();
    } catch {
      // ignore
    }
    try {
      task.cancel();
    } catch {
      // ignore
    }
  });
  UploadTaskRegistry.clear();
  notifyUploadRegistryChanged();
};

interface ImageUploadEmbedAttrs {
  nodeId: string;
  src: string; // blob url initially, then final download URL
  isUploading: boolean;
  progress: number; // 0 - 100
  setLoading?: (loading: boolean) => void | undefined;
  ownerId?: string; // editor instance id
  uploadFolder?: string; // storage sub-folder under workspaces/{id}/; defaults to DEFAULT_UPLOAD_FOLDER
}

const ImageUploadNodeView: React.FC<NodeViewProps> = ({ node, updateAttributes }) => {
  const { nodeId, src, isUploading, progress, setLoading, ownerId, uploadFolder } = node.attrs as ImageUploadEmbedAttrs;

  useEffect(() => {
    // Start upload once on mount
    if (!isUploading) return;

    const file = PastedFileRegistry.get(nodeId);
    const isFinalUrl = typeof src === 'string' && /^https?:\/\//.test(src);

    // If there's no file registered (e.g., after revisit) or src is already a final URL,
    // correct the node state so UI does not show uploading/progress.
    if (!file || isFinalUrl) {
      updateAttributes({ isUploading: false, progress: isFinalUrl ? 100 : 0 });
      return;
    }

    // Set loading state at the start of the upload
    if (typeof setLoading === 'function') {
      setLoading(true);
    }

    const workspace = UserPreferenceSingleton.getInstance().getWorkspace();
    const storage = getFirebaseStorage();

    let isMounted = true;
    let unsubscribe: (() => void) | null = null;

    const cleanup = () => {
      isMounted = false;
      if (typeof setLoading === 'function') {
        setLoading(false);
      }
    };

    const startUpload = async () => {
      // Keep pasted images at full quality: untouched if within 2000px, only resized if wider.
      const uploadFile = await compressImageFile(file, {
        maxWidth: IMAGE_COMPRESSION.DEFAULT_MAX_WIDTH,
        quality: IMAGE_COMPRESSION.EDITOR_QUALITY
      });
      if (!isMounted) return;

      const extension = uploadFile.name?.split('.').pop()?.toLowerCase() || 'png';
      const uniqueSuffix = Date.now().toString(36);
      const sanitizedBase = uploadFile.name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9_-]/g, '_');
      const fullFilename = `${sanitizedBase}_${uniqueSuffix}.${extension}`;

      const folder = uploadFolder || DEFAULT_UPLOAD_FOLDER;
      const basePath = workspace?.id ? `workspaces/${workspace.id}/${folder}` : folder;
      const sRef = storageRef(storage, `${basePath}${fullFilename}`);

      const uploadTask = uploadBytesResumable(sRef, uploadFile, { contentType: uploadFile.type });

      const onError = () => {
        if (!isMounted) return;
        updateAttributes({ isUploading: false });
        cleanup();
        // Remove the stale registry entry so isAnyImageUploading() reflects the failure
        // and listeners (e.g. submit gates) are notified. Idempotent if also run in onComplete.
        UploadTaskRegistry.delete(nodeId);
        NodeOwner.delete(nodeId);
        notifyUploadRegistryChanged();
      };

      const onComplete = async () => {
        if (!isMounted) return;

        try {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          if (!isMounted) return;

          updateAttributes({
            src: url,
            isUploading: false,
            progress: 100
          });
          PastedFileRegistry.delete(nodeId);
        } catch (e) {
          captureException(e);
          onError();
        } finally {
          cleanup();
          UploadTaskRegistry.delete(nodeId);
          notifyUploadRegistryChanged();
        }
      };

      unsubscribe = uploadTask.on(
        'state_changed',
        (snapshot) => {
          if (!isMounted) return;
          const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          updateAttributes({ progress: percent });
        },
        onError,
        onComplete
      );

      // The component may have unmounted while compression was running; stop now.
      if (!isMounted) {
        try {
          unsubscribe();
        } catch {
          // ignore
        }
        try {
          uploadTask.cancel();
        } catch {
          // ignore
        }
        return;
      }

      // Register controller for external control
      UploadTaskRegistry.set(nodeId, { task: uploadTask, unsubscribe });
      if (ownerId) NodeOwner.set(nodeId, ownerId);
      notifyUploadRegistryChanged();
    };

    startUpload();

    return () => {
      isMounted = false;
      try {
        unsubscribe?.();
      } catch {
        // ignore
      }
      UploadTaskRegistry.delete(nodeId);
      NodeOwner.delete(nodeId);
      notifyUploadRegistryChanged();
      cleanup();
    };
  }, [isUploading, nodeId, ownerId, setLoading, src, updateAttributes, uploadFolder]);

  return (
    <NodeViewWrapper>
      <RenderImagePreview
        imageUrl={src}
        altText='Uploaded image'
        isImage={true}
        type='Image'
        ShowImageOptions={true}
        ShowImageBorder={true}
        progress={progress}
        isUploading={isUploading}
      />
    </NodeViewWrapper>
  );
};

export const ImageUploadEmbed = Node.create({
  name: 'imageUploadEmbed',
  group: 'block',
  content: '', // Empty content to prevent any content inside
  defining: true,
  atom: true,
  selectable: true,
  draggable: false,
  inline: false,

  addAttributes() {
    return {
      nodeId: { default: '' },
      src: { default: '' },
      isUploading: { default: true },
      progress: { default: 0 },
      setLoading: { default: null },
      ownerId: { default: '' },
      // Only needed while the image is uploading; never persisted to the saved HTML.
      uploadFolder: { default: DEFAULT_UPLOAD_FOLDER, renderHTML: () => ({}) }
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="image-upload-embed"]',
        getAttrs: (dom: HTMLElement) => ({
          nodeId: dom.getAttribute('data-node-id') || '',
          src: dom.getAttribute('data-src') || '',
          isUploading: dom.getAttribute('data-uploading') === 'true',
          progress: Number(dom.getAttribute('data-progress') || 0),
          setLoading: null, // This won't be serialized to HTML
          ownerId: '',
          uploadFolder: DEFAULT_UPLOAD_FOLDER
        })
      },
      // Add support for regular img tags
      {
        tag: 'img',
        getAttrs: (dom: HTMLElement) => {
          const src = dom.getAttribute('src') || '';
          return {
            nodeId: nanoid(), // Generate a new ID for existing images
            src: src,
            isUploading: false,
            progress: 100,
            setLoading: null,
            ownerId: '',
            uploadFolder: DEFAULT_UPLOAD_FOLDER
          };
        }
      }
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'image-upload-embed',
        contenteditable: 'false',
        'data-drag-handle': ''
      })
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageUploadNodeView);
  }
});
